import { useRef, useCallback } from 'react'

export function useThrottle<T extends (...args: any[]) => any>(
  callback: T,
  delay: number
): T {
  const lastCallTime = useRef<number>(0)
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)

  const throttledCallback = useCallback(
    (...args: Parameters<T>) => {
      const now = Date.now()
      
      // Clear any pending timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }

      // If enough time has passed, execute immediately
      if (now - lastCallTime.current >= delay) {
        lastCallTime.current = now
        return callback(...args)
      }

      // Otherwise, schedule execution
      timeoutRef.current = setTimeout(() => {
        lastCallTime.current = Date.now()
        callback(...args)
        timeoutRef.current = null
      }, delay - (now - lastCallTime.current))
    },
    [callback, delay]
  ) as T

  return throttledCallback
}
